/**
 * Self-test suite for the per-thread chat export feature.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/ChatExportSelfTest.swift` in
 * intent but covers the web-specific surface: pure formatters with
 * a `LocalRoster`-injected display-name resolver, a passphrase seal
 * built on SubtleCrypto, and a pure policy gate.
 *
 * Registered with `isVerified: true` — verified against Chrome on Windows
 * 2026-05-21 and promoted to `[x]` in `Signal_standard_web.md`.
 *
 * Cases (organised top-to-bottom by the spec):
 *
 *   Formatter (plain text)
 *     - 3 records → header + 3 lines, sentAt ascending
 *     - input out-of-order → output is sorted
 *
 *   Formatter (JSON Lines)
 *     - each non-header line parses as valid JSON
 *     - keys within each object are alphabetically sorted
 *       (determinism: encoding twice byte-for-byte identical)
 *     - top line is the header, subsequent lines are records
 *
 *   Display-name resolution
 *     - missing roster entry → "Unknown user" (peer-claimed string ignored)
 *
 *   Cap enforcement
 *     - 50_001 records → { ok: false, error: { kind: 'tooMany', count: 50001 } }
 *     - 50_000 records → formats fine
 *
 *   Policy
 *     - not-a-member → { ok: false, reason: 'requesterNotMember' }
 *     - disabled-channel → { ok: false, reason: 'channelExportDisabled' }
 *
 *   Passphrase seal
 *     - roundtrip recovers identical input
 *     - magic distinct from `"kaether-backup-v1"` (cross-decode rejected)
 *     - two seals of same plaintext+passphrase → different ciphertexts
 *
 * Suite name: "Chat export (formatter + policy + seal)".
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import type { ChatExportRecord, LocalRoster } from "./ChatExportRecord";
import {
  formatJsonLines,
  formatPlainText,
  MAX_MESSAGES_PER_EXPORT,
} from "./ChatExportFormatter";
import { canExport } from "./ChatExportPolicy";
import type { ChatExportChannel, ChatExportRequester } from "./ChatExportPolicy";
import {
  CHAT_EXPORT_SEAL_MAGIC,
  ChatExportOpenError,
  open,
  seal,
} from "./ChatExportPassphraseSeal";

// ---------------------------------------------------------------------------
// Fixtures
// ---------------------------------------------------------------------------

const ROSTER: LocalRoster = {
  "user-alice": "Alice",
  "user-bob": "Bob",
};

function makeRecord(
  messageId: string,
  fromUserId: string,
  sentAt: number,
  body: string,
  overrides: Partial<ChatExportRecord> = {},
): ChatExportRecord {
  return {
    messageId,
    fromUserId,
    fromDisplayName: ROSTER[fromUserId] ?? "?",
    sentAt,
    body,
    ...overrides,
  };
}

const SAMPLE_RECORDS: ChatExportRecord[] = [
  makeRecord("m1", "user-alice", 1_700_000_000_000, "hello"),
  makeRecord("m2", "user-bob", 1_700_000_060_000, "hi back"),
  makeRecord("m3", "user-alice", 1_700_000_120_000, "third message"),
];

// ---------------------------------------------------------------------------
// Plain-text formatter
// ---------------------------------------------------------------------------

function testPlainTextThreeRecordsAscending(): TestCase {
  const name = "plaintext: 3 records → header + 3 lines, sentAt ascending";
  const r = formatPlainText(SAMPLE_RECORDS, {
    channelTitle: "Test Channel",
    roster: ROSTER,
    exportedAt: 1_700_000_500_000,
  });
  if (!r.ok) return fail(name, `formatter returned error: ${JSON.stringify(r)}`);
  const lines = r.output.split("\n");
  if (lines.length !== 4) {
    return fail(name, `wanted 4 lines (header + 3), got ${lines.length}: ${r.output}`);
  }
  const headerOk = lines[0].startsWith("# kaether chat export — Test Channel");
  // sentAt ascending: m1 (1.7B) < m2 (1.7B+60s) < m3 (1.7B+120s)
  const orderOk =
    lines[1].includes("hello") &&
    lines[2].includes("hi back") &&
    lines[3].includes("third message");
  return headerOk && orderOk
    ? pass(name)
    : fail(name, `headerOk=${headerOk} orderOk=${orderOk} got:\n${r.output}`);
}

function testPlainTextSortsOutOfOrderInput(): TestCase {
  const name = "plaintext: out-of-order input → output sorted by sentAt";
  // Reverse the sample records.
  const reversed = [...SAMPLE_RECORDS].reverse();
  const r = formatPlainText(reversed, {
    channelTitle: "Test Channel",
    roster: ROSTER,
    exportedAt: 1_700_000_500_000,
  });
  if (!r.ok) return fail(name, `formatter returned error: ${JSON.stringify(r)}`);
  const lines = r.output.split("\n");
  const orderOk =
    lines[1].includes("hello") &&
    lines[2].includes("hi back") &&
    lines[3].includes("third message");
  return orderOk
    ? pass(name)
    : fail(name, `output not sorted:\n${r.output}`);
}

// ---------------------------------------------------------------------------
// JSON Lines formatter
// ---------------------------------------------------------------------------

function testJsonLinesEachLineValidJson(): TestCase {
  const name = "json-lines: every line parses as valid JSON";
  const r = formatJsonLines(SAMPLE_RECORDS, {
    channelTitle: "Test Channel",
    roster: ROSTER,
    exportedAt: 1_700_000_500_000,
  });
  if (!r.ok) return fail(name, `formatter returned error: ${JSON.stringify(r)}`);
  const lines = r.output.split("\n");
  if (lines.length !== 4) {
    return fail(name, `wanted 4 lines, got ${lines.length}`);
  }
  for (let i = 0; i < lines.length; i++) {
    try {
      JSON.parse(lines[i]);
    } catch (e) {
      return fail(
        name,
        `line ${i} did not parse: ${lines[i]} — ${e instanceof Error ? e.message : String(e)}`,
      );
    }
  }
  return pass(name);
}

function testJsonLinesKeysAlphabeticalDeterminism(): TestCase {
  const name = "json-lines: keys are alphabetical (determinism — encoded twice byte-identical)";
  const opts = {
    channelTitle: "Test Channel",
    roster: ROSTER,
    exportedAt: 1_700_000_500_000,
  };
  // Build a deliberately-shuffled record set so any
  // object-iteration-order leak in the formatter shows up.
  const shuffled: ChatExportRecord[] = [
    SAMPLE_RECORDS[2],
    SAMPLE_RECORDS[0],
    SAMPLE_RECORDS[1],
  ];
  const a = formatJsonLines(SAMPLE_RECORDS, opts);
  const b = formatJsonLines(shuffled, opts);
  if (!a.ok || !b.ok) {
    return fail(name, `formatter returned error: ${JSON.stringify({ a, b })}`);
  }
  if (a.output !== b.output) {
    return fail(name, "two encodes of same input produced different bytes");
  }
  // Spot-check: header line ALWAYS appears with keys in alphabetical order
  // (channelTitle, exportedAt, schemaVersion).
  const lines = a.output.split("\n");
  const headerKeys = Object.keys(JSON.parse(lines[0]));
  const headerOrdered = headerKeys.join(",") === "channelTitle,exportedAt,schemaVersion";
  if (!headerOrdered) {
    return fail(name, `header keys not alphabetical: ${headerKeys.join(",")}`);
  }
  // Spot-check first record: keys (body, fromDisplayName, fromUserId, messageId, sentAt).
  const recordKeys = Object.keys(JSON.parse(lines[1]));
  const recordOrdered =
    recordKeys.join(",") ===
    "body,fromDisplayName,fromUserId,messageId,sentAt";
  if (!recordOrdered) {
    return fail(name, `record keys not alphabetical: ${recordKeys.join(",")}`);
  }
  return pass(name);
}

function testJsonLinesHeaderThenRecords(): TestCase {
  const name = "json-lines: top line is the header, subsequent lines are records";
  const r = formatJsonLines(SAMPLE_RECORDS, {
    channelTitle: "Test Channel",
    roster: ROSTER,
    exportedAt: 1_700_000_500_000,
  });
  if (!r.ok) return fail(name, `formatter returned error: ${JSON.stringify(r)}`);
  const lines = r.output.split("\n");
  let header: { channelTitle?: string; schemaVersion?: number; exportedAt?: number };
  try {
    header = JSON.parse(lines[0]);
  } catch (e) {
    return fail(name, `header not JSON: ${lines[0]}`);
  }
  if (header.channelTitle !== "Test Channel" || header.schemaVersion !== 1) {
    return fail(name, `header shape wrong: ${JSON.stringify(header)}`);
  }
  // First record line should have a messageId, not a schemaVersion.
  let rec: { messageId?: string; schemaVersion?: number };
  try {
    rec = JSON.parse(lines[1]);
  } catch {
    return fail(name, `line 1 not JSON: ${lines[1]}`);
  }
  if (!rec.messageId || rec.schemaVersion !== undefined) {
    return fail(name, `line 1 is not a record: ${JSON.stringify(rec)}`);
  }
  return pass(name);
}

// ---------------------------------------------------------------------------
// Display-name resolution
// ---------------------------------------------------------------------------

function testDisplayNameFromRosterNotRecord(): TestCase {
  const name = "display name: missing roster entry → 'Unknown user' (peer-claimed string ignored)";
  // Build a record with a peer-claimed `fromDisplayName` AND a userId
  // that the roster does NOT contain. The formatter must render
  // "Unknown user" rather than the peer-claimed string.
  const peerClaimed = makeRecord(
    "evil",
    "user-not-in-roster",
    1_700_000_000_000,
    "payload",
    { fromDisplayName: "EVIL CLAIMED NAME" },
  );
  const r = formatPlainText([peerClaimed], {
    channelTitle: "ch",
    roster: ROSTER, // does NOT contain user-not-in-roster
    exportedAt: 1_700_000_500_000,
  });
  if (!r.ok) return fail(name, `formatter returned error: ${JSON.stringify(r)}`);
  if (r.output.includes("EVIL CLAIMED NAME")) {
    return fail(
      name,
      `peer-claimed display name leaked into output:\n${r.output}`,
    );
  }
  if (!r.output.includes("Unknown user")) {
    return fail(name, `expected 'Unknown user' fallback, got:\n${r.output}`);
  }
  // Also verify the JSON Lines path.
  const j = formatJsonLines([peerClaimed], {
    channelTitle: "ch",
    roster: ROSTER,
    exportedAt: 1_700_000_500_000,
  });
  if (!j.ok) return fail(name, `json formatter returned error`);
  if (j.output.includes("EVIL CLAIMED NAME")) {
    return fail(
      name,
      `peer-claimed display name leaked into JSON Lines output`,
    );
  }
  if (!j.output.includes("Unknown user")) {
    return fail(name, `expected 'Unknown user' fallback in JSON output`);
  }
  return pass(name);
}

// ---------------------------------------------------------------------------
// Cap enforcement
// ---------------------------------------------------------------------------

function testCap50001Rejected(): TestCase {
  const name =
    "cap: 50_001 records → { ok: false, error: { kind: 'tooMany', count: 50001 } }";
  const huge: ChatExportRecord[] = Array.from(
    { length: MAX_MESSAGES_PER_EXPORT + 1 },
    (_, i) => makeRecord(`m${i}`, "user-alice", 1_700_000_000_000 + i, "x"),
  );
  // Plain text path.
  const p = formatPlainText(huge, {
    channelTitle: "ch",
    roster: ROSTER,
  });
  if (p.ok) return fail(name, "plaintext: ok=true, expected ok=false");
  if (p.error.kind !== "tooMany" || p.error.count !== 50_001) {
    return fail(name, `plaintext error wrong: ${JSON.stringify(p.error)}`);
  }
  // JSON Lines path.
  const j = formatJsonLines(huge, {
    channelTitle: "ch",
    roster: ROSTER,
  });
  if (j.ok) return fail(name, "jsonlines: ok=true, expected ok=false");
  if (j.error.kind !== "tooMany" || j.error.count !== 50_001) {
    return fail(name, `jsonlines error wrong: ${JSON.stringify(j.error)}`);
  }
  return pass(name);
}

function testCap50000Accepted(): TestCase {
  const name = "cap: 50_000 records → formats fine";
  const right: ChatExportRecord[] = Array.from(
    { length: MAX_MESSAGES_PER_EXPORT },
    (_, i) => makeRecord(`m${i}`, "user-alice", 1_700_000_000_000 + i, "x"),
  );
  const p = formatPlainText(right, {
    channelTitle: "ch",
    roster: ROSTER,
  });
  if (!p.ok) {
    return fail(name, `plaintext: ok=false at cap edge: ${JSON.stringify(p)}`);
  }
  const j = formatJsonLines(right, {
    channelTitle: "ch",
    roster: ROSTER,
  });
  if (!j.ok) {
    return fail(name, `jsonlines: ok=false at cap edge`);
  }
  return pass(name);
}

// ---------------------------------------------------------------------------
// Policy
// ---------------------------------------------------------------------------

function makeChannel(overrides: Partial<ChatExportChannel> = {}): ChatExportChannel {
  return {
    channelId: "ch-1",
    memberUserIds: ["user-alice", "user-bob"],
    isExportDisabled: false,
    messageCount: 10,
    ...overrides,
  };
}

function makeRequester(userId: string): ChatExportRequester {
  return { userId };
}

function testPolicyNotMember(): TestCase {
  const name = "policy: not-a-member → { ok: false, reason: 'requesterNotMember' }";
  const d = canExport(makeChannel(), makeRequester("user-not-in-channel"));
  if (d.ok || d.reason !== "requesterNotMember") {
    return fail(name, `got ${JSON.stringify(d)}`);
  }
  return pass(name);
}

function testPolicyDisabled(): TestCase {
  const name = "policy: disabled-channel → { ok: false, reason: 'channelExportDisabled' }";
  const d = canExport(
    makeChannel({ isExportDisabled: true }),
    makeRequester("user-alice"),
  );
  if (d.ok || d.reason !== "channelExportDisabled") {
    return fail(name, `got ${JSON.stringify(d)}`);
  }
  return pass(name);
}

// ---------------------------------------------------------------------------
// Passphrase seal
// ---------------------------------------------------------------------------

async function testSealRoundtrip(): Promise<TestCase> {
  const name = "seal: roundtrip recovers identical input";
  try {
    const plaintext = new TextEncoder().encode("hello chat export");
    const sealed = await seal(plaintext, "correct-horse-battery-staple", 1_700_000_500_000);
    const opened = await open(sealed, "correct-horse-battery-staple");
    if (opened.length !== plaintext.length) {
      return fail(
        name,
        `length mismatch: got ${opened.length}, want ${plaintext.length}`,
      );
    }
    for (let i = 0; i < plaintext.length; i++) {
      if (opened[i] !== plaintext[i]) {
        return fail(name, `byte ${i} mismatch`);
      }
    }
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

async function testSealMagicDistinctFromBackup(): Promise<TestCase> {
  const name =
    "seal: magic 'kaether-chat-export-v1' distinct from 'kaether-backup-v1' (cross-decode rejected)";
  // Verify our magic is what we think it is.
  if (CHAT_EXPORT_SEAL_MAGIC !== "kaether-chat-export-v1") {
    return fail(name, `our magic wrong: ${CHAT_EXPORT_SEAL_MAGIC}`);
  }
  // Build a plausible-looking file with the LOCAL-BACKUP magic prefix
  // (the magic that the parallel local-backup folder uses) and verify
  // our `open()` refuses it. We don't care that the rest of the file
  // is well-formed — auth fails on the magic check first.
  const fakeBackupMagic = new TextEncoder().encode("kaether-backup-v1");
  // Pad up to a length that passes the "too short" check (need at
  // least header (60 bytes) + GCM tag (16 bytes) = 76 bytes).
  const padded = new Uint8Array(200);
  padded.set(fakeBackupMagic, 0);
  // Fill the rest with zeros — irrelevant, the magic check fires first.
  try {
    await open(padded, "any-passphrase");
    return fail(name, "open() accepted a file with backup magic");
  } catch (e) {
    if (e instanceof ChatExportOpenError) {
      return pass(name);
    }
    return fail(
      name,
      `wrong error type: ${e instanceof Error ? e.message : String(e)}`,
    );
  }
}

async function testSealFreshSaltAndNonce(): Promise<TestCase> {
  const name =
    "seal: two seals of same plaintext + passphrase → different ciphertexts (fresh salt + nonce)";
  try {
    const plaintext = new TextEncoder().encode("payload");
    const passphrase = "same-passphrase";
    const sealedAt = 1_700_000_500_000;
    const a = await seal(plaintext, passphrase, sealedAt);
    const b = await seal(plaintext, passphrase, sealedAt);
    if (a.length !== b.length) {
      return pass(name); // lengths differing also proves divergence, but unlikely
    }
    let differs = false;
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) {
        differs = true;
        break;
      }
    }
    return differs
      ? pass(name)
      : fail(name, "two seals produced byte-identical ciphertexts");
  } catch (e) {
    return fail(name, `threw: ${e instanceof Error ? e.message : String(e)}`);
  }
}

// ---------------------------------------------------------------------------
// Suite registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Chat export (formatter + policy + seal)",
  isVerified: true,
  run: async (): Promise<ReturnType<typeof buildSuiteResult>> => {
    const cases: TestCase[] = [
      // Formatter — plain text
      testPlainTextThreeRecordsAscending(),
      testPlainTextSortsOutOfOrderInput(),
      // Formatter — JSON Lines
      testJsonLinesEachLineValidJson(),
      testJsonLinesKeysAlphabeticalDeterminism(),
      testJsonLinesHeaderThenRecords(),
      // Display-name resolution
      testDisplayNameFromRosterNotRecord(),
      // Cap enforcement
      testCap50001Rejected(),
      testCap50000Accepted(),
      // Policy
      testPolicyNotMember(),
      testPolicyDisabled(),
      // Passphrase seal
      await testSealRoundtrip(),
      await testSealMagicDistinctFromBackup(),
      await testSealFreshSaltAndNonce(),
    ];
    return buildSuiteResult(cases);
  },
});
